import clsx from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { revalidateDocument, type DocumentResultResponse } from '../api';
import { reviewerQueryKeys } from '../hooks/useDocumentResult';
import { useToastStore } from '../../../stores/toastStore';

interface ReValidateButtonProps {
  /** Only visible when the document is in `review_required`. */
  visible: boolean;
  /** Job id required to call `POST /documents/{id}/revalidate`. */
  jobId: string;
}

/**
 * Prominent amber CTA that re-runs the deterministic validation + scoring layers (no IA call)
 * via `POST /documents/{id}/revalidate` (Wave 6.C). On success the refreshed
 * `DocumentResultResponse` is written into the React Query cache so the banner and confidence
 * numbers update without a round-trip. The button is hidden when the document is not in
 * `review_required`.
 */
export function ReValidateButton({ visible, jobId }: ReValidateButtonProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((state) => state.push);

  const mutation = useMutation({
    mutationFn: () => revalidateDocument(jobId),
    onSuccess: (refreshed: DocumentResultResponse) => {
      // Replace the cached result so StatusBanner and FieldPanel re-render with the new status +
      // confidence numbers without a refetch. Match the key used by useDocumentResult exactly.
      queryClient.setQueryData(reviewerQueryKeys.result(jobId), refreshed);
      pushToast({
        tone: 'success',
        title: 'Documento revalidado',
        description:
          refreshed.status === 'approved'
            ? 'Todas las reglas se cumplen — el documento queda aprobado.'
            : 'Se ha recalculado la confianza con las correcciones manuales.',
        durationMs: 3_500,
      });
    },
    onError: (cause: unknown) => {
      pushToast({
        tone: 'error',
        title: 'No se pudo revalidar el documento',
        description: cause instanceof Error ? cause.message : 'Inténtalo de nuevo.',
      });
    },
  });

  if (!visible) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className={clsx(
        'inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold',
        'bg-amber-500 text-white shadow-sm transition-colors',
        'hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-70',
      )}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
        <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
        <path d="M3 21v-5h5" />
      </svg>
      {mutation.isPending ? 'Revalidando…' : 'Re-validar documento'}
    </button>
  );
}
